import { getAuthToken } from './supabase';
import type {
  Property,
  PropertyDetail,
  RentRollEntry,
  OperatingExpense,
  LoanTerms,
  AnalyticsData,
  PropertyFormData,
  RentRollFormEntry,
  ExpenseFormEntry,
  LoanTermsFormData,
  Firm,
  FirmStaff,
  ListingMedia,
} from './types';

async function authHeaders(): Promise<Record<string, string>> {
  const token = await getAuthToken();
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

async function handleResponse<T>(res: Response): Promise<T> {
  if (!res.ok) {
    const body = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error(body.error || `HTTP ${res.status}`);
  }
  return res.json() as Promise<T>;
}

// Properties
export async function fetchListings(preview?: string | null): Promise<(Property & { photoUrl: string | null })[]> {
  const qs = preview ? `?preview=${encodeURIComponent(preview)}` : '';
  const res = await fetch(`/api/listings${qs}`);
  return handleResponse<(Property & { photoUrl: string | null })[]>(res);
}

export async function updateListing(id: number, data: { isListed?: boolean; listingStatus?: string; latitude?: string; longitude?: string }): Promise<Property> {
  const res = await fetch(`/api/properties/${id}/listing`, {
    method: 'PATCH',
    headers: await authHeaders(),
    body: JSON.stringify(data),
  });
  return handleResponse<Property>(res);
}

export async function geocodeAddress(address: string): Promise<{ lat: string; lng: string } | null> {
  const key = import.meta.env.VITE_GOOGLE_MAPS_API_KEY as string;
  if (!key) return null;
  try {
    const res = await fetch(
      `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(address)}&key=${key}`
    );
    const data = await res.json() as { status: string; results: Array<{ geometry: { location: { lat: number; lng: number } } }> };
    if (data.status === 'OK' && data.results[0]) {
      return {
        lat: data.results[0].geometry.location.lat.toString(),
        lng: data.results[0].geometry.location.lng.toString(),
      };
    }
  } catch { /* non-fatal */ }
  return null;
}

export async function fetchMyProperties(): Promise<Property[]> {
  const res = await fetch('/api/my-properties', { headers: await authHeaders() });
  return handleResponse<Property[]>(res);
}

export async function fetchProperty(id: number): Promise<PropertyDetail> {
  const res = await fetch(`/api/properties/${id}`, { headers: await authHeaders() });
  return handleResponse<PropertyDetail>(res);
}

// "Tell me what you need" (server/routes/buyerRequests.ts)
export async function fetchBuyerRequestConfig<T>(): Promise<T> {
  const res = await fetch('/api/buyer-request/config');
  return handleResponse<T>(res);
}

export async function submitBuyerRequest(body: Record<string, unknown>): Promise<{ ok: boolean; status: number; body: Record<string, any> }> {
  const res = await fetch('/api/buyer-request', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
  });
  return { ok: res.ok, status: res.status, body: await res.json().catch(() => ({})) };
}

// `preview` carries a not-yet-public firm's pitch token through; without it the server
// answers 404 for that firm's listings.
function previewQs(preview?: string | null) {
  return preview ? `?preview=${encodeURIComponent(preview)}` : '';
}

export async function fetchPropertyByToken(token: string, preview?: string | null): Promise<PropertyDetail> {
  const res = await fetch(`/api/properties/share/${token}${previewQs(preview)}`);
  return handleResponse<PropertyDetail>(res);
}

export async function createProperty(data: PropertyFormData & { mapPinLink?: string }): Promise<Property> {
  const res = await fetch('/api/properties', {
    method: 'POST',
    headers: await authHeaders(),
    body: JSON.stringify(data),
  });
  return handleResponse<Property>(res);
}

export async function updateProperty(id: number, data: Partial<PropertyFormData> & { mapPinLink?: string }): Promise<Property> {
  const res = await fetch(`/api/properties/${id}`, {
    method: 'PUT',
    headers: await authHeaders(),
    body: JSON.stringify(data),
  });
  return handleResponse<Property>(res);
}

export async function deleteProperty(id: number): Promise<void> {
  const res = await fetch(`/api/properties/${id}`, {
    method: 'DELETE',
    headers: await authHeaders(),
  });
  return handleResponse<void>(res);
}

// Admin: permanently remove a user (login + all their data)
export async function deleteUserAccount(id: string): Promise<void> {
  const res = await fetch(`/api/admin/users/${id}`, {
    method: 'DELETE',
    headers: await authHeaders(),
  });
  return handleResponse<void>(res);
}

// Rent Roll
export async function addTenant(propertyId: number, data: RentRollFormEntry): Promise<RentRollEntry> {
  const res = await fetch(`/api/properties/${propertyId}/rent-roll`, {
    method: 'POST',
    headers: await authHeaders(),
    body: JSON.stringify(data),
  });
  return handleResponse<RentRollEntry>(res);
}

export async function updateTenant(propertyId: number, tenantId: number, data: RentRollFormEntry): Promise<RentRollEntry> {
  const res = await fetch(`/api/properties/${propertyId}/rent-roll/${tenantId}`, {
    method: 'PUT',
    headers: await authHeaders(),
    body: JSON.stringify(data),
  });
  return handleResponse<RentRollEntry>(res);
}

export async function deleteTenant(propertyId: number, tenantId: number): Promise<void> {
  const res = await fetch(`/api/properties/${propertyId}/rent-roll/${tenantId}`, {
    method: 'DELETE',
    headers: await authHeaders(),
  });
  return handleResponse<void>(res);
}

// Expenses
export async function addExpense(propertyId: number, data: ExpenseFormEntry): Promise<OperatingExpense> {
  const res = await fetch(`/api/properties/${propertyId}/expenses`, {
    method: 'POST',
    headers: await authHeaders(),
    body: JSON.stringify(data),
  });
  return handleResponse<OperatingExpense>(res);
}

export async function deleteExpense(propertyId: number, expenseId: number): Promise<void> {
  const res = await fetch(`/api/properties/${propertyId}/expenses/${expenseId}`, {
    method: 'DELETE',
    headers: await authHeaders(),
  });
  return handleResponse<void>(res);
}

// Loan Terms
export async function saveLoanTerms(propertyId: number, data: LoanTermsFormData): Promise<LoanTerms> {
  const res = await fetch(`/api/properties/${propertyId}/loan`, {
    method: 'PUT',
    headers: await authHeaders(),
    body: JSON.stringify(data),
  });
  return handleResponse<LoanTerms>(res);
}

// Analytics
export async function trackView(propertyId: number, ranAnalysis: boolean = false): Promise<void> {
  await fetch(`/api/properties/${propertyId}/analytics`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ ranAnalysis }),
  }).catch(() => {}); // fire and forget
}

export async function fetchAnalytics(propertyId: number): Promise<AnalyticsData> {
  const res = await fetch(`/api/properties/${propertyId}/analytics`, { headers: await authHeaders() });
  return handleResponse<AnalyticsData>(res);
}

// Documents
export interface DocumentRecord {
  id: number;
  propertyId: number;
  fileName: string;
  fileUrl: string;
  fileSize: number | null;
  mimeType: string | null;
  documentType: string;
  source: string;
  isPublic: boolean;
  parentDocumentId: number | null;
  description: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface LeaseAbstract {
  tenantName: string;
  landlordName: string;
  suite: string;
  squareFootage: number;
  baseRentMonthly: number;
  baseRentAnnual: number;
  rentPerSF: number;
  leaseStartDate: string;
  leaseEndDate: string;
  leaseTerm: string;
  rentEscalations: string;
  permittedUse: string;
  cam: {
    recoveryStructure: string | null;
    baseYear: string | null;
    proportionateShare: string | null;
    expenseCap: string | null;
    capitalTreatment: string | null;
    landlordResponsibilities: string | null;
    tenantResponsibilities: string | null;
    hvac: string | null;
    estimatedCamPerSF: number | null;
    buyerTakeaway: string | null;
  } | null;
  options: {
    renewal: string | null;
    expansion: string | null;
    rofr: string | null;
  } | null;
  coTenancyClauses: string | null;
  terminationRights: string | null;
  guarantor: string | null;
  securityDeposit: number | null;
  tiAllowance: number | null;
  parkingSpaces: string | null;
  specialProvisions: string | null;
  amendmentNotes?: string | null;
  summary: string;
}

export async function fetchDocuments(propertyId: number): Promise<DocumentRecord[]> {
  const res = await fetch(`/api/properties/${propertyId}/documents`, { headers: await authHeaders() });
  return handleResponse<DocumentRecord[]>(res);
}

export async function fetchPublicDocuments(propertyId: number, preview?: string | null): Promise<DocumentRecord[]> {
  const res = await fetch(`/api/properties/${propertyId}/documents/public${previewQs(preview)}`);
  return handleResponse<DocumentRecord[]>(res);
}

export async function uploadDocument(propertyId: number, file: File, documentType: string): Promise<DocumentRecord> {
  const token = await getAuthToken();
  const formData = new FormData();
  formData.append('file', file);
  formData.append('documentType', documentType);
  const res = await fetch(`/api/properties/${propertyId}/documents/upload`, {
    method: 'POST',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: formData,
  });
  return handleResponse<DocumentRecord>(res);
}

// ── Bulk flyer import ──
export interface BulkDraft {
  ok: boolean;
  fileName: string;
  flyerUrl?: string;
  error?: string;
  duplicateOf?: { id: number; name: string };  // matches an existing listing by address
  fields: {
    name?: string; address?: string; city?: string; state?: string; zip?: string;
    propertyType?: string; dealType?: string;
    squareFootage?: number | null; acreage?: number | null; askingPrice?: number | null;
    sellerNote?: string;
    broker?: { name?: string; phone?: string; email?: string };
    businessName?: string;
  };
}

export async function bulkExtractFlyers(files: File[]): Promise<{ drafts: BulkDraft[] }> {
  const token = await getAuthToken();
  const fd = new FormData();
  files.forEach((f) => fd.append('files', f));
  const res = await fetch('/api/bulk-import/extract', {
    method: 'POST',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: fd,
  });
  return handleResponse<{ drafts: BulkDraft[] }>(res);
}

export async function uploadBulkThumbnail(blob: Blob): Promise<string> {
  const token = await getAuthToken();
  const fd = new FormData();
  fd.append('file', blob, 'thumb.jpg');
  const res = await fetch('/api/bulk-import/thumbnail', {
    method: 'POST',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: fd,
  });
  const d = await handleResponse<{ url: string }>(res);
  return d.url;
}

export async function bulkPublishListings(rows: Record<string, unknown>[]): Promise<{ created: Array<{ id: number; shareToken: string; name: string }>; count: number }> {
  const res = await fetch('/api/bulk-import/publish', {
    method: 'POST',
    headers: await authHeaders(),
    body: JSON.stringify({ rows }),
  });
  return handleResponse(res);
}

export async function toggleDocumentVisibility(documentId: number, isPublic: boolean): Promise<DocumentRecord> {
  const res = await fetch(`/api/documents/${documentId}/visibility`, {
    method: 'PATCH',
    headers: await authHeaders(),
    body: JSON.stringify({ isPublic }),
  });
  return handleResponse<DocumentRecord>(res);
}

export async function saveDocumentContent(documentId: number, html: string): Promise<void> {
  const res = await fetch(`/api/documents/${documentId}/content`, {
    method: 'PUT',
    headers: await authHeaders(),
    body: JSON.stringify({ html }),
  });
  return handleResponse<void>(res);
}

export async function deleteDocument(documentId: number): Promise<void> {
  const res = await fetch(`/api/documents/${documentId}`, {
    method: 'DELETE',
    headers: await authHeaders(),
  });
  return handleResponse<void>(res);
}

export async function extractLeaseFields(file: File): Promise<{
  tenantName: string | null;
  suite: string | null;
  squareFootage: number | null;
  monthlyRent: number | null;
  leaseStart: string | null;
  leaseEnd: string | null;
  annualIncreases: number | null;
  annualRecovery: number | null;
}> {
  const token = await getAuthToken();
  const formData = new FormData();
  formData.append('file', file);
  const res = await fetch('/api/extract-lease-fields', {
    method: 'POST',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: formData,
  });
  return handleResponse(res);
}

export async function extractExpenseFields(file: File): Promise<Array<{
  category: string;
  amount: number;
  description: string;
}>> {
  const token = await getAuthToken();
  const formData = new FormData();
  formData.append('file', file);
  const res = await fetch('/api/extract-expense-fields', {
    method: 'POST',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: formData,
  });
  return handleResponse(res);
}

export async function uploadContactLogo(propertyId: number, file: File): Promise<{ contactLogoUrl: string }> {
  const token = await getAuthToken();
  const formData = new FormData();
  formData.append('logo', file);
  const res = await fetch(`/api/properties/${propertyId}/logo`, {
    method: 'POST',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: formData,
  });
  return handleResponse(res);
}

export async function extractTenantFromDocs(propertyId: number, documentIds: number[]): Promise<{
  tenantName: string | null;
  suite: string | null;
  squareFootage: number | null;
  monthlyRent: number | null;
  leaseStart: string | null;
  leaseEnd: string | null;
  annualIncreases: number | null;
  annualRecovery: number | null;
}> {
  const res = await fetch(`/api/properties/${propertyId}/extract-tenant-from-docs`, {
    method: 'POST',
    headers: await authHeaders(),
    body: JSON.stringify({ documentIds }),
  });
  return handleResponse(res);
}

export async function extractLeaseFieldsFromVault(documentId: number): Promise<{
  tenantName: string | null;
  suite: string | null;
  squareFootage: number | null;
  monthlyRent: number | null;
  leaseStart: string | null;
  leaseEnd: string | null;
  annualIncreases: number | null;
  annualRecovery: number | null;
}> {
  const res = await fetch(`/api/documents/${documentId}/extract-lease-fields`, {
    method: 'POST',
    headers: await authHeaders(),
  });
  return handleResponse(res);
}

export async function extractExpenseFieldsFromVault(documentId: number): Promise<Array<{
  category: string;
  amount: number;
  description: string;
}>> {
  const res = await fetch(`/api/documents/${documentId}/extract-expense-fields`, {
    method: 'POST',
    headers: await authHeaders(),
  });
  return handleResponse(res);
}

export async function generateLeaseAbstract(documentId: number): Promise<{ abstract: LeaseAbstract; document: DocumentRecord }> {
  const res = await fetch(`/api/documents/${documentId}/generate-abstract`, {
    method: 'POST',
    headers: await authHeaders(),
  });
  return handleResponse<{ abstract: LeaseAbstract; document: DocumentRecord }>(res);
}

// Saved Deals
export async function fetchSavedDeals(): Promise<{ id: number; savedAt: string; property: import('./types').Property }[]> {
  const res = await fetch('/api/saved-deals', { headers: await authHeaders() });
  return handleResponse(res);
}

export async function saveDeal(propertyId: number): Promise<void> {
  const res = await fetch('/api/saved-deals', {
    method: 'POST',
    headers: await authHeaders(),
    body: JSON.stringify({ propertyId }),
  });
  return handleResponse(res);
}

export async function unsaveDeal(propertyId: number): Promise<void> {
  const res = await fetch(`/api/saved-deals/${propertyId}`, {
    method: 'DELETE',
    headers: await authHeaders(),
  });
  return handleResponse(res);
}

export async function generateLeaseAbstractMulti(documentIds: number[], primaryDocumentId: number): Promise<{ abstract: LeaseAbstract; document: DocumentRecord }> {
  const res = await fetch('/api/documents/generate-abstract-multi', {
    method: 'POST',
    headers: await authHeaders(),
    body: JSON.stringify({ documentIds, primaryDocumentId }),
  });
  return handleResponse<{ abstract: LeaseAbstract; document: DocumentRecord }>(res);
}

export async function getEmailBlastPreview(propertyId: number, customMessage: string): Promise<{ html: string }> {
  const params = new URLSearchParams({ customMessage });
  const res = await fetch(`/api/properties/${propertyId}/email-blast/preview?${params}`, {
    headers: await authHeaders(),
  });
  return handleResponse<{ html: string }>(res);
}

export async function sendTestEmail(propertyId: number, subject: string, customMessage: string): Promise<{ success: boolean; sentTo: string }> {
  const res = await fetch(`/api/properties/${propertyId}/email-blast/test`, {
    method: 'POST',
    headers: { ...await authHeaders(), 'Content-Type': 'application/json' },
    body: JSON.stringify({ subject, customMessage }),
  });
  return handleResponse<{ success: boolean; sentTo: string }>(res);
}

export async function sendEmailBlast(propertyId: number, subject: string, customMessage: string, customEmails?: string[], mode?: 'castle' | 'custom' | 'both'): Promise<{ success: boolean; sent: number }> {
  const res = await fetch(`/api/properties/${propertyId}/email-blast`, {
    method: 'POST',
    headers: { ...await authHeaders(), 'Content-Type': 'application/json' },
    body: JSON.stringify({ subject, customMessage, customEmails, mode }),
  });
  return handleResponse<{ success: boolean; sent: number }>(res);
}

export async function saveUserEmailList(emails: string[], propertyId?: number, source?: string): Promise<void> {
  await fetch('/api/user-email-lists', {
    method: 'POST',
    headers: { ...await authHeaders(), 'Content-Type': 'application/json' },
    body: JSON.stringify({ emails, propertyId, source }),
  });
  // fire-and-forget — don't block the user if this fails
}

// Admin: broker email list
export async function getBrokerEmails(): Promise<{ emails: Array<{ id: number; email: string; createdAt: string }>; count: number }> {
  const res = await fetch('/api/broker-emails', { headers: await authHeaders() });
  return handleResponse(res);
}

export async function addBrokerEmail(email: string): Promise<void> {
  const res = await fetch('/api/broker-emails', {
    method: 'POST',
    headers: { ...await authHeaders(), 'Content-Type': 'application/json' },
    body: JSON.stringify({ email }),
  });
  await handleResponse(res);
}

export async function batchAddBrokerEmails(emails: string[]): Promise<{ inserted: number }> {
  const res = await fetch('/api/broker-emails/batch', {
    method: 'POST',
    headers: { ...await authHeaders(), 'Content-Type': 'application/json' },
    body: JSON.stringify({ emails }),
  });
  return handleResponse(res);
}

// Admin: activity
export async function fetchUserActivity(): Promise<{ users: Array<{ id: string; email: string; lastLogin: string | null; createdAt: string; propertyCount: number; blastsSent: number }> }> {
  const res = await fetch('/api/admin/user-activity', { headers: await authHeaders() });
  return handleResponse(res);
}

export type AgentHitRow = { agent: string; kind: string; hits: number; active_days: number; hosts: number; last_seen: string; last_path: string | null };
export type AgentHostRow = { host: string; hits: number; ai: number; search: number; seo: number };
export type AgentSectionRow = { section: string; hits: number; ai: number; search: number; seo: number; agents: number };

export async function fetchAgentHits(days = 30): Promise<{
  days: number;
  byAgent: AgentHitRow[];
  byKind: Array<{ kind: string; hits: number }>;
  byHost: AgentHostRow[];
  bySection: AgentSectionRow[];
}> {
  const res = await fetch(`/api/admin/agent-hits?days=${days}`, { headers: await authHeaders() });
  return handleResponse(res);
}

export async function fetchEmailLists(): Promise<{ lists: Array<{ userId: string; userEmail: string | null; emailCount: number; emails: string[]; lastSubmitted: string; submissionCount: number }> }> {
  const res = await fetch('/api/admin/email-lists', { headers: await authHeaders() });
  return handleResponse(res);
}

export async function deleteBrokerEmail(id: number): Promise<void> {
  const res = await fetch(`/api/broker-emails/${id}`, {
    method: 'DELETE',
    headers: await authHeaders(),
  });
  await handleResponse(res);
}

// ─── Billing ──────────────────────────────────────────────────────────────────

export interface BillingStatus {
  status: 'none' | 'admin' | 'comp' | 'trialing' | 'active' | 'past_due' | 'canceled' | 'incomplete';
  active: boolean;
  currentPeriodEnd?: string;
  cancelAtPeriodEnd?: boolean;
}

export async function fetchBillingStatus(): Promise<BillingStatus> {
  const res = await fetch('/api/billing/status', { headers: await authHeaders() });
  return handleResponse<BillingStatus>(res);
}

export async function createCheckoutSession(): Promise<{ url: string }> {
  const res = await fetch('/api/billing/create-checkout', {
    method: 'POST',
    headers: await authHeaders(),
  });
  return handleResponse<{ url: string }>(res);
}

export async function createPortalSession(): Promise<{ url: string }> {
  const res = await fetch('/api/billing/portal', {
    method: 'POST',
    headers: await authHeaders(),
  });
  return handleResponse<{ url: string }>(res);
}

// ── Firm backend (Vanessa admin) ──────────────────────────────────────────────

export async function fetchFirmBranding(slug: string): Promise<{ name: string; slug: string; siteUrl: string | null; logoUrl: string | null; primaryColor: string | null } | null> {
  const res = await fetch(`/api/public/firm/${slug}`);
  if (!res.ok) return null;
  return res.json();
}

export async function fetchMyFirms(): Promise<{ firms: Firm[]; isGlobalAdmin: boolean }> {
  const res = await fetch('/api/firms/mine', { headers: await authHeaders() });
  return handleResponse<{ firms: Firm[]; isGlobalAdmin: boolean }>(res);
}

export async function fetchFirmStaff(firmId: number): Promise<FirmStaff[]> {
  const res = await fetch(`/api/firms/${firmId}/staff`, { headers: await authHeaders() });
  const data = await handleResponse<{ staff: FirmStaff[] }>(res);
  return data.staff;
}

export async function createFirmStaff(firmId: number, data: Partial<FirmStaff>): Promise<FirmStaff> {
  const res = await fetch(`/api/firms/${firmId}/staff`, {
    method: 'POST',
    headers: await authHeaders(),
    body: JSON.stringify(data),
  });
  return (await handleResponse<{ staff: FirmStaff }>(res)).staff;
}

export async function updateFirmStaff(firmId: number, id: number, data: Partial<FirmStaff>): Promise<FirmStaff> {
  const res = await fetch(`/api/firms/${firmId}/staff/${id}`, {
    method: 'PATCH',
    headers: await authHeaders(),
    body: JSON.stringify(data),
  });
  return (await handleResponse<{ staff: FirmStaff }>(res)).staff;
}

export async function deleteFirmStaff(firmId: number, id: number): Promise<void> {
  const res = await fetch(`/api/firms/${firmId}/staff/${id}`, {
    method: 'DELETE',
    headers: await authHeaders(),
  });
  await handleResponse<{ success: true }>(res);
}

export async function reorderFirmStaff(firmId: number, orderedIds: number[]): Promise<void> {
  const res = await fetch(`/api/firms/${firmId}/staff/reorder`, {
    method: 'POST',
    headers: await authHeaders(),
    body: JSON.stringify({ orderedIds }),
  });
  await handleResponse<{ success: true }>(res);
}

export async function uploadStaffHeadshot(firmId: number, id: number, file: File): Promise<FirmStaff> {
  const token = await getAuthToken();
  const form = new FormData();
  form.append('file', file);
  const res = await fetch(`/api/firms/${firmId}/staff/${id}/headshot`, {
    method: 'POST',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: form,
  });
  return (await handleResponse<{ staff: FirmStaff }>(res)).staff;
}

export async function fetchFirmListings(firmId: number): Promise<Property[]> {
  const res = await fetch(`/api/firms/${firmId}/listings`, { headers: await authHeaders() });
  return (await handleResponse<{ listings: Property[] }>(res)).listings;
}

export async function createFirmListing(firmId: number, data: Partial<Property>): Promise<Property> {
  const res = await fetch(`/api/firms/${firmId}/listings`, {
    method: 'POST',
    headers: await authHeaders(),
    body: JSON.stringify(data),
  });
  return (await handleResponse<{ listing: Property }>(res)).listing;
}

export async function updateFirmListing(firmId: number, id: number, data: Partial<Property>): Promise<Property> {
  const res = await fetch(`/api/firms/${firmId}/listings/${id}`, {
    method: 'PATCH',
    headers: await authHeaders(),
    body: JSON.stringify(data),
  });
  return (await handleResponse<{ listing: Property }>(res)).listing;
}

export async function deleteFirmListing(firmId: number, id: number): Promise<void> {
  const res = await fetch(`/api/firms/${firmId}/listings/${id}`, {
    method: 'DELETE',
    headers: await authHeaders(),
  });
  await handleResponse<{ success: true }>(res);
}

export async function uploadListingMedia(firmId: number, listingId: number, file: File): Promise<ListingMedia[]> {
  const token = await getAuthToken();
  const form = new FormData();
  form.append('file', file);
  const res = await fetch(`/api/firms/${firmId}/listings/${listingId}/media`, {
    method: 'POST',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: form,
  });
  return (await handleResponse<{ media: ListingMedia[] }>(res)).media;
}

export async function deleteListingMedia(firmId: number, listingId: number, mediaId: number): Promise<ListingMedia[]> {
  const res = await fetch(`/api/firms/${firmId}/listings/${listingId}/media/${mediaId}`, {
    method: 'DELETE',
    headers: await authHeaders(),
  });
  return (await handleResponse<{ media: ListingMedia[] }>(res)).media;
}

export async function uploadListingFlyer(firmId: number, listingId: number, file: File): Promise<{ flyerUrl: string; flyerName: string }> {
  const token = await getAuthToken();
  const form = new FormData();
  form.append('file', file);
  const res = await fetch(`/api/firms/${firmId}/listings/${listingId}/flyer`, {
    method: 'POST',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: form,
  });
  return handleResponse<{ flyerUrl: string; flyerName: string }>(res);
}

export async function deleteListingFlyer(firmId: number, listingId: number): Promise<void> {
  const res = await fetch(`/api/firms/${firmId}/listings/${listingId}/flyer`, {
    method: 'DELETE',
    headers: await authHeaders(),
  });
  await handleResponse<{ success: true }>(res);
}

export async function fetchListingGallery(token: string, preview?: string | null): Promise<{ photos: string[]; videos: string[] }> {
  const res = await fetch(`/api/public/listing/${token}${previewQs(preview)}`);
  if (!res.ok) return { photos: [], videos: [] };
  const d = await res.json();
  return {
    photos: (d.photos || []).map((p: { url: string }) => p.url),
    videos: (d.videos || []).map((v: { url: string }) => v.url),
  };
}

export async function extractFlyerFields(firmId: number, file: File): Promise<Record<string, unknown>> {
  const token = await getAuthToken();
  const form = new FormData();
  form.append('file', file);
  const res = await fetch(`/api/firms/${firmId}/listings/extract`, {
    method: 'POST',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: form,
  });
  return (await handleResponse<{ fields: Record<string, unknown> }>(res)).fields;
}


// ── Property screening (multi-rubric) ──
// The screen is admin-only. A single candidate can be shared by token; that endpoint is
// public by design and returns exactly one row (see server/routes/screen.ts).

export interface WsGate {
  id: string; label: string; requirement: string; actual: string;
  passed: boolean; unknown: boolean;
}
export interface WsFactor {
  id: string; label: string; weight: number; ratio: number;
  points: number; actual: string; target: string;
}
export interface WsCandidate {
  id: number; batch_id: number; address: string;
  city: string | null; state: string | null; zip: string | null;
  lat: number | null; lon: number | null;
  building_sf: number | null; vacant_sf: number | null;
  clear_height: string | null; parking_ratio: string | null;
  asset_type: string | null; year_built: number | null; asking_price: string | null;
  ring_income: number | null; ring_households: number | null;
  ring_confidence: string | null; market_households: number | null; smb_count: number | null;
  dist_nearest_ws_mi: string | null; nearest_ws_name: string | null;
  passed: boolean | null; score: number | null; base_score: number | null;
  adjustment: { multiplier: number; reason: string } | null;
  unknown_count: number | null;
  gates: WsGate[] | null; factors: WsFactor[] | null; failures: string[] | null;
  rubric_id: string | null; rubric_name: string | null;
  rubric_version: string | null; share_token: string | null;
}
export interface WsBatch {
  id: number; name: string; source_file: string | null; rubric_id: string;
  row_count: number; candidates: number; passing: number; scored: number; shared: number; created_at: string;
}
export interface RubricInfo {
  id: string; name: string; client: string; version: string; summary: string;
}

export async function fetchRubrics(): Promise<{ rubrics: RubricInfo[]; defaultId: string }> {
  const res = await fetch('/api/screen/rubrics', { headers: await authHeaders() });
  return handleResponse(res);
}

export async function uploadWsBatch(file: File, rubricId: string, name?: string): Promise<{
  batch: WsBatch & { inserted: number };
  skipped: number;
  skipReasons: string[];
  mappedColumns: Record<string, string>;
  headersSeen: string[];
}> {
  const token = await getAuthToken();
  const fd = new FormData();
  fd.append('file', file);
  fd.append('rubricId', rubricId);
  if (name) fd.append('name', name);
  const res = await fetch('/api/screen/batches', {
    method: 'POST',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: fd,
  });
  return handleResponse(res);
}

/** Scores one chunk. Call until `remaining` is 0 — see scoreWsBatchAll. */
export async function scoreWsBatch(batchId: number, limit = 40): Promise<{
  scored: number; geocodeFailed: number; errored: number; errors: string[];
  total: number; remaining: number; rubric: string;
}> {
  const res = await fetch(`/api/screen/batches/${batchId}/score?limit=${limit}`, {
    method: 'POST', headers: await authHeaders(),
  });
  return handleResponse(res);
}

/**
 * Drives the chunk loop to completion, reporting progress.
 * Stops if a chunk scores nothing while rows remain — otherwise a row that always throws
 * would be retried forever.
 */
export async function scoreWsBatchAll(
  batchId: number,
  onProgress: (done: number, total: number, note: string) => void,
): Promise<{ scored: number; geocodeFailed: number; errored: number; total: number; stalled: boolean }> {
  let scored = 0, geocodeFailed = 0, errored = 0, total = 0, stalled = false;
  for (;;) {
    const r = await scoreWsBatch(batchId);
    scored += r.scored; geocodeFailed += r.geocodeFailed; errored += r.errored; total = r.total;
    const done = r.total - r.remaining;
    onProgress(done, r.total,
      `${done} of ${r.total} scored` +
      (geocodeFailed ? ` · ${geocodeFailed} not geocoded` : '') +
      (errored ? ` · ${errored} failed` : ''));
    if (r.remaining === 0) break;
    if (r.scored === 0) { stalled = true; break; }
  }
  return { scored, geocodeFailed, errored, total, stalled };
}

export async function fetchWsBatches(): Promise<{ batches: WsBatch[] }> {
  const res = await fetch('/api/screen/batches', { headers: await authHeaders() });
  return handleResponse(res);
}

export async function fetchWsCandidates(batchId: number): Promise<{ candidates: WsCandidate[] }> {
  const res = await fetch(`/api/screen/batches/${batchId}`, { headers: await authHeaders() });
  return handleResponse(res);
}

export async function deleteWsBatch(id: number): Promise<{
  ok: boolean;
  deleted: { name: string; candidates: number; sharedLinksBroken: number };
}> {
  const res = await fetch(`/api/screen/batches/${id}`, {
    method: 'DELETE', headers: await authHeaders(),
  });
  return handleResponse(res);
}

export async function shareWsCandidate(id: number): Promise<{ shareToken: string }> {
  const res = await fetch(`/api/screen/candidates/${id}/share`, {
    method: 'POST', headers: await authHeaders(),
  });
  return handleResponse(res);
}

export async function unshareWsCandidate(id: number): Promise<{ ok: boolean }> {
  const res = await fetch(`/api/screen/candidates/${id}/share`, {
    method: 'DELETE', headers: await authHeaders(),
  });
  return handleResponse(res);
}

/** Public — no auth header, this is the shareable artifact. */
export async function fetchWsReport(token: string): Promise<{ report: WsCandidate }> {
  const res = await fetch(`/api/screen/report/${token}`);
  return handleResponse(res);
}

// Admin: inquiries (broker-contact clicks, buyer requests, AI fetches of segment listings)
export type InquiryMonth = { month: string; segmentClicks: number; allClicks: number; aiClicks: number; internal: number; requests: number };
export type InquiryListing = { id: number; name: string; city: string; inSegment: boolean; clicks: number; aiClicks: number; internal: number; aiFetches30d: number };
export type InquiryRequest = { id: number; createdAt: string; name: string; email: string | null; phone: string | null; useType: string | null; sizeRange: string | null; area: string | null; budget: string | null; timing: string | null; emailSent: boolean; fromAi: boolean };

export async function fetchInquiries(): Promise<{ unsentRequests: number; months: InquiryMonth[]; listings: InquiryListing[]; requests: InquiryRequest[] }> {
  const res = await fetch('/api/admin/inquiries', { headers: await authHeaders() });
  return handleResponse(res);
}
